import CardCommon from '@op/components/CardCommon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MentionsSentimentSubSortType,
  mentionsSentimentTab,
  MentionsSentimentTabType,
  TableHeight,
} from '../constants';
import { Select } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import TableCommonHook from '@op/components/TableCommonHook';
import useCompareKeyMetricsColumns, {
  getBaseList,
} from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';
import { useCompareKeyMetricsTable } from '../hooks';
import {
  CompetitorStateListType,
  dealwithMentionsSentimentTable,
} from '../utils';
import { SortBy } from 'tea-component/lib/table/addons';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

export default function MentionsSentimentTable({
  filter,
  tab,
  gameData,
}: {
  filter: any;
  tab: MentionsSentimentTabType;
  gameData: {
    [id: string]: {
      name: string;
      color: string;
    };
  }[];
}) {
  const [byType, setByType] =
    useState<MentionsSentimentSubSortType>('by_mentions');
  const [sorts, setSorts] = useState<SortBy<any>>({
    by: '',
    order: 'desc',
  });
  const { formatOptionsMessage, formatMessage } = useLanguage();
  const baseColumnRef = useRef<Array<string>>([]);
  const { downloadLegacy, downloading } = useDownloadParams();

  const options = useMemo(() => {
    const item = mentionsSentimentTab.find((item) => item.value === tab);
    const options = item?.options || [];
    setByType(options[0]?.value || 'by_channel');
    return options;
  }, [tab]);

  const params = useMemo(() => {
    const result = {
      ...filter,
      stats_types: ['mentions_sentiment'],
      tab_type: tab,
      by_type: byType,
    };
    if (['by_channel', 'by_language'].includes(byType)) {
      result.top_n = 5;
    }

    return result;
  }, [byType, filter, tab]);

  const { tableRecords, loading } = useCompareKeyMetricsTable({
    filter: params,
    dealwithToTable: (data: CompetitorStateListType, filter: any) => {
      const tableList = dealwithMentionsSentimentTable(data, filter);
      if (['by_channel', 'by_language'].includes(filter.by_type)) {
        const mainInfo = data.find(
          (item) => item.edition_unified_id === filter.edition_unified_id,
        );
        const mainData: any = mainInfo?.list || {};
        const baseColumn = Object.keys(mainData);
        baseColumn.sort((a, b) => {
          return mainData[b] - mainData[a];
        });
        baseColumnRef.current = baseColumn;
        setOriginColumns(
          getBaseList(baseColumn, filter.by_type, tab),
          tableList.length,
        );
      } else {
        setOriginColumns([], tableList.length);
      }

      return tableList;
    },
    gameData,
    defaultSortKey: 'overall',
  });

  useEffect(() => {
    if (
      ['by_channel', 'by_language'].includes(byType) &&
      baseColumnRef.current.length > 0
    ) {
      setOriginColumns(
        getBaseList(baseColumnRef.current, byType, tab),
        tableRecords?.length || 0,
      );
    } else {
      setOriginColumns([], tableRecords?.length || 0);
    }
  }, [formatMessage, byType]);

  const { columns, sortColumns, setOriginColumns } =
    useCompareKeyMetricsColumns({
      oneKey: 'MentionsSentiment',
      twoKey: tab,
      threeKey: byType,
      isAuto: false,
      gameData,
      tableRecords,
      filter,
    });

  return (
    <CardCommon
      contentCard
      className="mentions-sentiment-table-wrapper"
      title={
        <Select
          appearance="button"
          options={formatOptionsMessage(options)}
          value={byType}
          style={{ width: 192, fontWeight: 400 }}
          listWidth={192}
          onChange={(value: MentionsSentimentSubSortType) => {
            setByType(value);
            buriedBtnPoint('CKMS_ByMetricsType', value);
          }}
        ></Select>
      }
      loading={loading}
      loadingHeight={300}
      hasDownload
      downloadLoading={downloading}
      handleDownloadOnClick={() => {
        downloadLegacy({
          params: {
            ...params,
            baseColumn: baseColumnRef.current,
            gameData,
          },
          downloadFunc: 'getOverviewCompetitorTable',
          sheetName: 'Compare Key Metrics Mentions & Sentiment Table',
          simpleName: 'Compare Key Metrics Mentions & Sentiment Table',
        });
      }}
      logBtnId="Mentions & Sentiment Table"
    >
      <TableCommonHook
        columns={columns}
        recordKey="edition_unified_id"
        records={tableRecords}
        hasPageable={false}
        maxHeight={TableHeight.BELOW}
        sortColumns={sortColumns}
        defaultSort={sorts}
        bordered="all"
        sortType={'local'}
        sortInfo={[sorts, setSorts]}
        logBtnId={'Mentions & Sentiment'}
      ></TableCommonHook>
    </CardCommon>
  );
}
